import { apiUrl } from "../config/api";

const CONTENT_ADMIN_URL = apiUrl("travel-admin/insurance-blog");

const findErrorMessage = (value) => {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (Array.isArray(value)) {
    for (const item of value) {
      const message = findErrorMessage(item);
      if (message) return message;
    }
  } else if (value && typeof value === "object") {
    for (const key of ["detail", "message", "non_field_errors"]) {
      const message = findErrorMessage(value[key]);
      if (message) return message;
    }
    for (const nested of Object.values(value)) {
      const message = findErrorMessage(nested);
      if (message) return message;
    }
  }
  return "";
};

const parseResponse = async (response) => {
  if (response.status === 204) return {};
  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) return response.json();
  const text = await response.text();
  return text && !/<html[\s>]/i.test(text) ? { message: text } : {};
};

const request = async (url, options = {}, fallbackMessage) => {
  const response = await fetch(url, options);
  const data = await parseResponse(response);
  if (!response.ok) {
    const error = new Error(findErrorMessage(data) || fallbackMessage);
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return data;
};

const authHeaders = (token) => ({
  Accept: "application/json",
  ...(token && token !== "undefined" ? { Authorization: `Token ${token}` } : {}),
});

const queryString = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") query.set(key, value);
  });
  const serialized = query.toString();
  return serialized ? `?${serialized}` : "";
};

const adminRequest = (path, token, { method = "GET", body, params } = {}) =>
  request(
    `${CONTENT_ADMIN_URL}/${path.replace(/^\/+|\/+$/g, "")}/${queryString(params)}`,
    {
      method,
      headers: {
        ...authHeaders(token),
        ...(body === undefined ? {} : { "Content-Type": "application/json" }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    },
    "The content request could not be completed."
  );

const contentApi = {
  login(credentials) {
    return request(
      apiUrl("auth/"),
      {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify(credentials),
      },
      "Sign in failed."
    );
  },

  register(account) {
    return request(
      apiUrl("users/"),
      {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify(account),
      },
      "We could not create your account."
    );
  },

  getProfile(token) {
    return request(apiUrl("profile/"), { headers: authHeaders(token) }, "Your profile could not be loaded.");
  },

  getPosts(params, token) {
    return adminRequest("posts", token, { params });
  },

  getAnalytics(days = 30, token) {
    return adminRequest("analytics", token, { params: { days } });
  },

  getPost(postId, token) {
    return adminRequest(`posts/${encodeURIComponent(postId)}`, token);
  },

  createPost(post, token) {
    return adminRequest("posts", token, { method: "POST", body: post });
  },

  updatePost(postId, changes, token) {
    return adminRequest(`posts/${encodeURIComponent(postId)}`, token, {
      method: "PATCH",
      body: changes,
    });
  },

  deletePost(postId, token) {
    return adminRequest(`posts/${encodeURIComponent(postId)}`, token, { method: "DELETE" });
  },

  uploadImage(image, token) {
    const form = new FormData();
    form.append("image", image);
    return request(
      `${CONTENT_ADMIN_URL}/media/images/`,
      { method: "POST", headers: authHeaders(token), body: form },
      "The image could not be uploaded."
    );
  },
};

export default contentApi;
